/**
 * Unit domain API functions (Step 7.1.1).
 *
 * Unit CRUD, normalizer, address verification, geocoding.
 * Extracted from adminData/index.js for team scalability.
 */
import {
  API_BASE_URL,
  getApiHeaders,
  expectPaginatedItems,
  parseAdminErrorResponse,
} from "./core";

export function fetchAdminUnits() {
  return fetch(`${API_BASE_URL}/api/admin/units`, { headers: getApiHeaders() })
    .then((res) => {
      if (!res.ok) throw new Error("Units konnten nicht geladen werden.");
      return res.json();
    })
    .then((data) => expectPaginatedItems(data, "GET /api/admin/units"));
}

/**
 * GET /api/admin/units — FULL ``{items, total}`` envelope (C3).
 *
 * ``fetchAdminUnits`` above sends no parameters at all, so it silently
 * gets the server default of 50 rows AND unwraps the envelope to a bare
 * array. Both properties make it unusable as a picker source, and both
 * are relied upon by its existing callers — hence a second function
 * rather than a change.
 *
 * ``q`` is the search term added to the endpoint in C3 (short unit id,
 * title, address, city, property title).
 */
export function fetchAdminUnitsPage({ q, limit, offset } = {}) {
  const sp = new URLSearchParams();
  if (offset != null) sp.set("skip", String(offset));
  if (limit != null) sp.set("limit", String(limit));
  if (q != null && String(q).trim()) sp.set("q", String(q).trim());
  const qs = sp.toString();
  const url = `${API_BASE_URL}/api/admin/units${qs ? `?${qs}` : ""}`;
  return fetch(url, { headers: getApiHeaders() }).then(async (res) => {
    if (!res.ok) throw new Error("Units konnten nicht geladen werden.");
    const data = await res.json();
    const items = expectPaginatedItems(data, "GET /api/admin/units");
    return {
      items,
      total: typeof data?.total === "number" ? data.total : items.length,
    };
  });
}

/**
 * Backend page size for ``GET /api/admin/units``.
 *
 * 200 is the endpoint's own maximum (``limit: int = Query(50, ge=1, le=200)``
 * in ``routes_admin_units.py``), not a number picked to be "big enough" —
 * asking for more is rejected, and asking for less only costs round trips.
 */
const ADMIN_UNITS_PAGE_SIZE = 200;

/**
 * Runaway guard for the loop below, not a cap on how many units may exist.
 * A healthy response ends on a short page; this only catches a server that
 * never returns one, and it THROWS rather than returning a partial list.
 */
const ADMIN_UNITS_MAX_PAGES = 50;

/**
 * GET /api/admin/units — every unit in the org, paging until exhausted.
 *
 * ``fetchAdminUnits`` sends no parameters and therefore silently receives
 * the server default of 50 rows, which makes any org-wide count computed
 * from it quietly wrong past 50 units. This helper is the complete source:
 * it pages explicitly and returns the raw items in backend order
 * (``ORDER BY Unit.title, Unit.id``), applying no sort of its own.
 *
 * Mirrors ``fetchAdminTenanciesAll`` in tenancies.js: a page shorter than
 * the requested limit is the last one. HTTP failures propagate from
 * ``fetchAdminUnitsPage`` — a caller never receives a silently truncated
 * list.
 */
export async function fetchAdminUnitsAll(params = {}) {
  const limit = ADMIN_UNITS_PAGE_SIZE;
  let skip = 0;
  const all = [];
  for (let page = 0; ; page += 1) {
    if (page >= ADMIN_UNITS_MAX_PAGES) {
      throw new Error("Units konnten nicht vollständig geladen werden.");
    }
    const { items } = await fetchAdminUnitsPage({ ...params, limit, offset: skip });
    if (!Array.isArray(items) || items.length === 0) break;
    all.push(...items);
    if (items.length < limit) break;
    skip += limit;
  }
  return all;
}

export function fetchAdminUnit(id) {
  return fetch(`${API_BASE_URL}/api/admin/units/${encodeURIComponent(id)}`, {
    headers: getApiHeaders(),
  }).then((res) => {
    if (!res.ok) {
      if (res.status === 404) return null;
      throw new Error("Unit konnte nicht geladen werden.");
    }
    return res.json();
  });
}

export async function createAdminUnit(body) {
  const res = await fetch(`${API_BASE_URL}/api/admin/units`, {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

export async function updateAdminUnit(id, body) {
  const res = await fetch(`${API_BASE_URL}/api/admin/units/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: getApiHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

export async function deleteAdminUnit(id) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/units/${encodeURIComponent(id)}`,
    { method: "DELETE", headers: getApiHeaders() }
  );

  let responseData = null;
  try {
    responseData = await res.json();
  } catch (_) {}

  if (!res.ok) {
    let msg =
      responseData && typeof responseData.detail === "string"
        ? responseData.detail
        : "";

    if (!msg && res.status === 400) {
      msg =
        "Unit kann nicht gelöscht werden, da noch verknüpfte Daten vorhanden sind.";
    }

    if (!msg) {
      msg = `HTTP ${res.status}`;
    }

    throw new Error(msg);
  }

  return responseData || { status: "ok" };
}

/**
 * Normalize unit from API for pages that expect unitId, place, etc.
 */
export function normalizeUnit(u) {
  if (!u) return u;
  return {
    ...u,
    unitId: u.unitId ?? u.id,
    shortUnitId: u.shortUnitId ?? u.short_unit_id ?? "",
    place: u.place ?? u.city ?? u.address ?? "",
    tenantPriceMonthly: u.tenantPriceMonthly ?? u.tenant_price_monthly_chf ?? "",
    landlordRentMonthly: u.landlordRentMonthly ?? u.landlord_rent_monthly_chf ?? "",
    utilitiesMonthly: u.utilitiesMonthly ?? u.utilities_monthly_chf ?? "",
    cleaningCostMonthly: u.cleaningCostMonthly ?? u.cleaning_cost_monthly_chf ?? "",
    landlordLeaseStartDate: u.landlordLeaseStartDate ?? u.landlord_lease_start_date ?? "",
    availableFrom: u.availableFrom ?? u.available_from ?? "",
    // Step 5.2: no more "Frei" default — real occupancy is from occupancy_service / backend.
    status: u.status ?? u.occupancy_status ?? null,
    occupiedRooms: u.occupiedRooms ?? u.occupied_rooms ?? 0,
    zip: u.zip ?? u.postal_code ?? "",
    landlordDepositType: u.landlordDepositType ?? u.landlord_deposit_type ?? "",
    landlordDepositAmount:
      u.landlordDepositAmount ?? u.landlord_deposit_amount ?? "",
    landlordDepositAnnualPremium:
      u.landlordDepositAnnualPremium ?? u.landlord_deposit_annual_premium ?? "",
    landlordDepositProvider:
      u.landlordDepositProvider ?? u.landlord_deposit_provider ?? "",
    landlord_id: u.landlord_id ?? null,
    property_manager_id: u.property_manager_id ?? null,
    owner_id: u.owner_id ?? null,
    ownerName: u.ownerName ?? u.owner_name ?? null,
    leaseType: u.leaseType ?? u.lease_type ?? "",
    leaseStartDate: u.leaseStartDate ?? u.lease_start_date ?? "",
    leaseEndDate: u.leaseEndDate ?? u.lease_end_date ?? "",
    noticeGivenDate: u.noticeGivenDate ?? u.notice_given_date ?? "",
    terminationEffectiveDate:
      u.terminationEffectiveDate ?? u.termination_effective_date ?? "",
    returnedToLandlordDate:
      u.returnedToLandlordDate ?? u.returned_to_landlord_date ?? "",
    leaseStatus: u.leaseStatus ?? u.lease_status ?? "",
    leaseNotes: u.leaseNotes ?? u.lease_notes ?? "",
  };
}

function _firstTrimmedString(...vals) {
  for (const v of vals) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return "";
}

/** True when label is only the same as city (avoid leading with city instead of a unit code). */
function _labelLooksLikeCityOnly(label, city) {
  const a = String(label || "").trim().toLowerCase();
  const b = String(city || "").trim().toLowerCase();
  return Boolean(a && b && a === b);
}

/**
 * Label for Unit select options: short id · address, PLZ Ort.
 * Leading token: explicit short-id fields (if present), else title/name unless duplicate of city, else UUID.
 * @param {object|null|undefined} u - Raw unit from API
 * @returns {string}
 */
export function formatUnitSelectOptionLabel(u) {
  if (!u) return "—";
  const nu = normalizeUnit(u);
  const idStr = String(nu.id || nu.unitId || "").trim();
  const uuidShort = idStr.length > 12 ? `${idStr.slice(0, 8)}…` : idStr;

  const city = String(nu.city || "").trim();

  const explicitShort = _firstTrimmedString(
    u.shortUnitId,
    u.unit_short_id,
    u.short_unit_id,
    u.unit_code,
    u.external_unit_id,
    nu.shortUnitId,
    nu.unit_short_id,
    nu.short_unit_id,
    nu.unit_code,
    nu.external_unit_id
  );

  let leading = explicitShort;
  if (!leading) {
    const t = String(nu.title || "").trim();
    if (t && !_labelLooksLikeCityOnly(t, city)) leading = t;
  }
  if (!leading) {
    const n = String(nu.name || "").trim();
    const t = String(nu.title || "").trim();
    if (n && !_labelLooksLikeCityOnly(n, city) && n !== t) leading = n;
  }
  if (!leading) {
    leading = uuidShort || "—";
  }

  const addr = String(nu.address || "").trim();
  const pc = String(nu.zip || nu.postal_code || "").trim();
  const placePart = [pc, city].filter(Boolean).join(" ");
  const tail = [addr, placePart].filter(Boolean).join(", ");
  if (tail) return `${leading} · ${tail}`;
  return leading;
}

export function verifyAdminAddress(body) {
  return fetch(`${API_BASE_URL}/api/admin/address/verify`, {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify({
      address_line1: body.address_line1 ?? "",
      postal_code: body.postal_code ?? "",
      city: body.city ?? "",
    }),
  }).then(async (res) => {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(
        typeof data.detail === "string" ? data.detail : "Adressprüfung fehlgeschlagen."
      );
    }
    return data;
  });
}

/**
 * POST /api/admin/units/{id}/geocode — manual geocoding retry (server-side).
 */
export function geocodeAdminUnit(id) {
  return fetch(`${API_BASE_URL}/api/admin/units/${encodeURIComponent(id)}/geocode`, {
    method: "POST",
    headers: getApiHeaders(),
  }).then((res) => {
    if (!res.ok) throw new Error("Geocoding-Anfrage fehlgeschlagen.");
    return res.json();
  });
}
